import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Receipt, FileText, ChevronDown, ChevronRight, AlertCircle, CheckCircle2, Send, Download, Calendar, FileDown } from "lucide-react";
import { API } from "@/contexts/AuthContext";
import { TeamBadge } from "./ClientsList";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Billing() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [marking, setMarking] = useState({});
  const [downloadingPdf, setDownloadingPdf] = useState({});
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (dateFrom) p.append("date_from", dateFrom);
    if (dateTo) p.append("date_to", dateTo);
    return p.toString();
  }, [dateFrom, dateTo]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API}/billing/unbilled${queryString ? `?${queryString}` : ""}`;
      const { data } = await axios.get(url);
      setGroups(data || []);
    } catch (e) {
      console.error("Failed to load billing groups:", e);
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => { load(); }, [load]);

  const toggle = (key) => setExpanded((s) => ({ ...s, [key]: !s[key] }));

  const markInvoiced = async (group) => {
    if (!window.confirm(`Mark ${group.tasks.length} tasks ($${group.total_amount.toFixed(2)}) as invoiced for ${group.client_name}?`)) return;
    setMarking((s) => ({ ...s, [group.key]: true }));
    try {
      await axios.post(`${API}/billing/mark-invoiced`, {
        task_ids: group.tasks.map((t) => t.id),
      });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't update tasks.");
    } finally {
      setMarking((s) => ({ ...s, [group.key]: false }));
    }
  };

  const downloadPdf = async (group) => {
    setDownloadingPdf((s) => ({ ...s, [group.key]: true }));
    try {
      const res = await axios.get(`${API}/billing/invoice-pdf?client_id=${group.client_id}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = extractFilename(res.headers["content-disposition"]) || `navica-invoice-${group.client_name.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("Couldn't generate PDF. Please try again.");
      console.error(e);
    } finally {
      setDownloadingPdf((s) => ({ ...s, [group.key]: false }));
    }
  };

  const downloadCsv = () => {
    // Use a real anchor with cookies; axios blob also works but anchor preserves browser download UX
    const url = `${BACKEND_URL}/api/billing/export.csv${queryString ? `?${queryString}` : ""}`;
    window.location.href = url;
  };

  const grandTotal = groups.reduce((s, g) => s + g.total_amount, 0);
  const grandHours = groups.reduce((s, g) => s + g.total_hours, 0);

  return (
    <div data-testid="billing-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-sm uppercase tracking-wider text-[#EA823A] font-semibold mb-2">Billing</div>
          <h1 className="font-display text-4xl text-[#1F2A2E]">Unbilled work</h1>
          <p className="text-[#4A595E] mt-2">Grouped by client and billing recipient. Each card is one potential draft invoice.</p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#FCEADB] text-[#A0521A] text-xs font-semibold">
          <AlertCircle className="w-3.5 h-3.5" /> Xero auto-push coming next
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-2xl border border-[#F0E5DE] p-4 mb-6 flex flex-wrap items-end gap-4" data-testid="billing-filters">
        <div className="flex items-center gap-2 text-[#284952]">
          <Calendar className="w-4 h-4 text-[#EA823A]" />
          <span className="text-xs uppercase tracking-wider font-semibold text-[#6B7A80]">Activity date range</span>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider font-semibold text-[#6B7A80] mb-1">From</label>
          <input type="date" className="input-navica !py-2" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider font-semibold text-[#6B7A80] mb-1">To</label>
          <input type="date" className="input-navica !py-2" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" />
        </div>
        {(dateFrom || dateTo) && (
          <button onClick={() => { setDateFrom(""); setDateTo(""); }} className="text-xs text-[#E2718A] hover:underline font-semibold" data-testid="filter-clear">
            Clear range
          </button>
        )}
        <div className="flex-1" />
        <button onClick={downloadCsv} className="btn-outline text-sm" data-testid="download-csv">
          <FileDown className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {/* Totals */}
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <SummaryCard label="Open groups" value={groups.length} />
        <SummaryCard label="Total hours" value={grandHours.toFixed(2)} suffix="hrs" />
        <SummaryCard label="Total to invoice" value={`$${grandTotal.toFixed(2)}`} accent />
      </div>

      {loading ? (
        <div className="text-center py-20"><div className="w-8 h-8 border-3 border-[#E2718A] border-t-transparent rounded-full animate-spin mx-auto" /></div>
      ) : groups.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border border-[#F0E5DE]" data-testid="billing-empty">
          <CheckCircle2 className="w-12 h-12 mx-auto text-[#1F6B3A] mb-4" />
          <h3 className="font-display text-xl text-[#1F2A2E] mb-2">All caught up</h3>
          <p className="text-[#6B7A80] text-sm">
            {dateFrom || dateTo ? "No unbilled tasks in this date range." : "No unbilled tasks across your caseload. Nice work."}
          </p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="billing-groups">
          {groups.map((g) => (
            <div key={g.key} className="bg-white rounded-3xl border border-[#F0E5DE] overflow-hidden" data-testid={`billing-group-${g.client_id}`}>
              <button onClick={() => toggle(g.key)} className="w-full text-left px-6 py-5 flex flex-wrap items-center justify-between gap-4 hover:bg-[#F1E4D5]/50 transition" data-testid={`billing-group-toggle-${g.client_id}`}>
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  {expanded[g.key] ? <ChevronDown className="w-5 h-5 text-[#6B7A80]" /> : <ChevronRight className="w-5 h-5 text-[#6B7A80]" />}
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <Link to={`/staff/clients/${g.client_id}`} className="font-display text-lg text-[#1F2A2E] hover:text-[#E2718A] transition" onClick={(e) => e.stopPropagation()}>
                        {g.client_name}
                      </Link>
                      <TeamBadge team={g.team || "ndis"} />
                    </div>
                    <div className="text-xs text-[#6B7A80] mt-1">
                      Bill to: <span className="font-semibold text-[#284952]">{g.billing_recipient_name}</span>
                      {g.billing_recipient_email && <span> · {g.billing_recipient_email}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-5 ml-auto">
                  <div className="text-right">
                    <div className="text-xs text-[#6B7A80]">{g.tasks.length} tasks · {g.total_hours.toFixed(2)} hrs</div>
                    <div className="font-display text-xl text-[#1F2A2E]">${g.total_amount.toFixed(2)}</div>
                  </div>
                </div>
              </button>

              {expanded[g.key] && (
                <div className="px-6 pb-6 border-t border-[#F0E5DE]">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm mt-3">
                      <thead className="text-left">
                        <tr>
                          <Th>Date</Th>
                          <Th>Activity</Th>
                          <Th>Hours</Th>
                          <Th>Rate</Th>
                          <Th>Amount</Th>
                          <Th>Worker</Th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#F0E5DE]">
                        {g.tasks.map((t) => (
                          <tr key={t.id}>
                            <td className="px-3 py-2 whitespace-nowrap">{t.activity_date}</td>
                            <td className="px-3 py-2">
                              <div className="font-medium">{t.rate_name}</div>
                              {t.description && <div className="text-xs text-[#6B7A80] mt-0.5 whitespace-pre-line">{t.description}</div>}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">{Number(t.hours).toFixed(2)}</td>
                            <td className="px-3 py-2 whitespace-nowrap">${Number(t.rate_amount).toFixed(2)}</td>
                            <td className="px-3 py-2 whitespace-nowrap font-semibold">${Number(t.amount).toFixed(2)}</td>
                            <td className="px-3 py-2 text-xs text-[#6B7A80] whitespace-nowrap">{t.worker_name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex flex-wrap gap-3 items-center justify-end pt-5 mt-5 border-t border-[#F0E5DE]">
                    {!g.billing_recipient && (
                      <div className="text-xs text-[#A0521A] flex items-center gap-1.5 mr-auto">
                        <AlertCircle className="w-3.5 h-3.5" /> Set a billing recipient on this client to send to Xero.
                      </div>
                    )}
                    <button
                      onClick={() => downloadPdf(g)}
                      disabled={downloadingPdf[g.key]}
                      className="btn-outline text-sm"
                      data-testid={`download-pdf-${g.client_id}`}
                    >
                      <Download className="w-4 h-4" /> {downloadingPdf[g.key] ? "Generating..." : "Invoice PDF"}
                    </button>
                    <button
                      onClick={() => markInvoiced(g)}
                      disabled={marking[g.key]}
                      className="btn-outline text-sm"
                      data-testid={`mark-invoiced-${g.client_id}`}
                    >
                      <FileText className="w-4 h-4" /> {marking[g.key] ? "Marking..." : "Mark as invoiced"}
                    </button>
                    <button
                      disabled
                      className="text-sm flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#F0E5DE] text-[#6B7A80] cursor-not-allowed font-semibold"
                      title="Xero integration coming soon"
                      data-testid={`xero-disabled-${g.client_id}`}
                    >
                      <Send className="w-4 h-4" /> Send to Xero (coming soon)
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, suffix, accent }) {
  return (
    <div className={`rounded-3xl p-5 border ${accent ? "bg-[#FCE7EB] border-[#F4C3CC]" : "bg-white border-[#F0E5DE]"}`}>
      <div className="text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1">{label}</div>
      <div className="font-display text-3xl text-[#1F2A2E]">
        {value} {suffix && <span className="text-sm text-[#6B7A80]">{suffix}</span>}
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-[#6B7A80]">{children}</th>;
}

function extractFilename(disposition) {
  if (!disposition) return null;
  const m = disposition.match(/filename="?([^"]+)"?/);
  return m ? m[1] : null;
}
